import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  usePathname,
  useParams,
  useSearchParams,
  useRouter,
} from 'next/navigation';
import { Link } from '@/shared/ui/link';
import { useI18n, useCurrentLocale } from '@locales/client';
import {
  StickyWrapper,
  ContentStyle,
  ContainerStyle,
  StyleInner,
  GlobalStyles,
} from './styles';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { MobileButton } from '../mobile-button';
import { MenuModal } from '../menu-modal';
import { useVisibleState } from '@/shared/hooks';
import { useBanners } from '@/entities/minibanner/model/hooks';
import LogoMobile2Row from '@/shared/assets/logo-mobile-2row.svg';
import { useFullscreen } from '@/flat-pages/game/FullscreenContext';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useNavigationGuard } from '@/flat-pages/game/NavigationGuardProvider';

type MobileMenuProps = {
  openWalletManagment?: () => void;
  mobileLandscape: boolean;
  $flexMain?: boolean;
};

type MenuItem = {
  id: number;
  tKey: string;
  image: string;
  srcActive?: string;
  disabled?: boolean;
  param: string;
};

const MobileMenu = ({
  openWalletManagment,
  mobileLandscape,
  $flexMain = false,
}: MobileMenuProps) => {
  const pathname = usePathname();
  const { user } = useUser();
  const params = useParams();
  const router = useRouter();
  const locale = useCurrentLocale();
  const t = useI18n();
  const { sortedMobileMenuBanners } = useBanners({ mobileLandscape });
  const searchParams = useSearchParams();
  const { open, visible, close } = useVisibleState(false);
  const [chatVisible, setChatVisible] = useState(false);
  const { headerEnabled } = useFullscreen();
  const { isFullscreen } = useFullscreen();
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const { requestNavigation } = useNavigationGuard();

  const searchParam = searchParams.get('search');

  const isPlayGame = useMemo(() => {
    return Boolean(params?.titleGame) || pathname.includes('/game/');
  }, [params, pathname]);

  useEffect(() => {
    return () => {
      window.LiveChatWidget?.call('hide');
    };
  }, []);

  // Эффект для открытия меню при поиске
  useEffect(() => {
    if (searchParam && `/${locale}` === pathname) {
      open();
    }
  }, [searchParam, pathname, locale, open]);

  const supportHandlerClick = useCallback(() => {
    window.LiveChatWidget?.call('maximize');
    setChatVisible(true);
  }, []);

  const profileHandlerClick = useCallback(() => {
    if (!user.isAuth && openWalletManagment) {
      openWalletManagment();
    }
  }, [user.isAuth, openWalletManagment]);

  const handleClick = useCallback(
    (el: MenuItem) => () => {
      if (el.disabled) return;

      switch (el.id) {
        case 16: // Поиск
          open();
          break;
        case 18: // Поддержка
          supportHandlerClick();
          break;
        case 19: // Профиль
          if (!user.isAuth) {
            profileHandlerClick();
          } else {
            requestNavigation(`/${locale}/${el.param}`);
            // router.push(`/${locale}/${el.param}`);
          }
          break;
        default:
          requestNavigation(`/${locale}/${el.param}`);
        // router.push(`/${locale}/${el.param}`);
      }
    },
    [
      open,
      supportHandlerClick,
      profileHandlerClick,
      router,
      locale,
      user.isAuth,
    ]
  );

  const menuButtons = useMemo(() => {
    return sortedMobileMenuBanners?.map((el) => (
      <MobileButton
        title={t(el.tKey)}
        handlerClick={handleClick(el)}
        src={el.image}
        srcActive={el.srcActive}
        key={el.id}
        disabled={el.disabled}
        $isActive={
          pathname?.includes(el.param) &&
          (!pathname.includes('games') || pathname.endsWith('games'))
        }
        $decored={
          (el.id === 17 || el.id === 20) &&
          (!pathname.includes('games') || pathname.endsWith('games'))
        }
      />
    ));
  }, [sortedMobileMenuBanners, t, handleClick, pathname]);

  if (isPlayGame) {
    // return null;
  }

  if (isFullscreen) return null;

  return (
    <>
      {!chatVisible && <GlobalStyles />}
      <StickyWrapper id="mobileMenu" $flexMain={$flexMain}>
        <ContentStyle>
          <StyleInner>
            <ContainerStyle>
              {!headerEnabled && isLandscape && (
                <Link
                  href={`/${locale}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 0',
                  }}
                >
                  <LogoMobile2Row />
                </Link>
              )}
              {menuButtons}
            </ContainerStyle>
          </StyleInner>
        </ContentStyle>
      </StickyWrapper>
      <MenuModal
        visible={visible}
        onClose={close}
        supportOpen={supportHandlerClick}
      />
    </>
  );
};

const MobileMenuWithBreakpointsSlice = withBreakpointsSlice(
  MobileMenu,
  ({ mobileLandscape, isPortrait }) => ({
    mobileLandscape,
    isPortrait,
  })
);

export { MobileMenuWithBreakpointsSlice as MobileMenu };
